// File: config/environments.js

/**
 * Centralized Environment Configuration
 * 
 * This file provides a single source of truth for all environment-specific values.
 * All components should import from this file rather than accessing process.env directly.
 * 
 * Usage:
 * import { ENV_CONFIG } from './config/environments.js';
 * console.log(ENV_CONFIG.APP_NAME);
 */

export const ENV_CONFIG = {
  // Application Settings
  APP_URL: getEnvVar('APP_URL', 'http://localhost:3000'),
  // Central CDRisk API — accounts (password/passkey/social login) and
  // annotation sync live here.
  API_URL: getEnvVar('API_URL', 'https://cdrisk-api-production.up.railway.app'),
  APP_NAME: getEnvVar('APP_NAME', 'OSHA Code Viewer'),
  APP_VERSION: getEnvVar('APP_VERSION', '1.0.0'),

  // Feature Flags
  ENABLE_OFFLINE: getEnvVar('ENABLE_OFFLINE', 'true') === 'true',
  ENABLE_ANALYTICS: getEnvVar('ENABLE_ANALYTICS', 'false') === 'true',
  ENABLE_PDF_EXPORT: getEnvVar('ENABLE_PDF_EXPORT', 'true') === 'true',
  ENABLE_BOOKMARKS: getEnvVar('ENABLE_BOOKMARKS', 'true') === 'true',
  ENABLE_NOTES: getEnvVar('ENABLE_NOTES', 'true') === 'true',
  // In-place Spanish regulation text. Off for now — section pages show a
  // "coming soon" notice until the translation data is backfilled.
  ENABLE_CONTENT_TRANSLATIONS: getEnvVar('ENABLE_CONTENT_TRANSLATIONS', 'false') === 'true',

  // Environment
  NODE_ENV: getEnvVar('NODE_ENV', 'development'),
  IS_PRODUCTION: getEnvVar('NODE_ENV', 'development') === 'production',
  IS_DEVELOPMENT: getEnvVar('NODE_ENV', 'development') === 'development',

  // Storage Settings
  STORAGE_PREFIX: getEnvVar('STORAGE_PREFIX', 'osha_viewer_'),
  MAX_BOOKMARKS: parseInt(getEnvVar('MAX_BOOKMARKS', '100'), 10),
  MAX_NOTES: parseInt(getEnvVar('MAX_NOTES', '500'), 10),

  // Language Settings
  DEFAULT_LANGUAGE: getEnvVar('DEFAULT_LANGUAGE', 'en'),
  SUPPORTED_LANGUAGES: getEnvVar('SUPPORTED_LANGUAGES', 'en,es').split(','),

  // Search Settings
  MAX_SEARCH_RESULTS: parseInt(getEnvVar('MAX_SEARCH_RESULTS', '50'), 10),
  SEARCH_DEBOUNCE_MS: parseInt(getEnvVar('SEARCH_DEBOUNCE_MS', '300'), 10),

  // UI Settings
  SCROLL_THRESHOLD_BACK_TO_TOP: parseInt(getEnvVar('SCROLL_THRESHOLD_BACK_TO_TOP', '300'), 10),
  ANIMATION_DURATION_MS: parseInt(getEnvVar('ANIMATION_DURATION_MS', '300'), 10),
  TOUCH_TARGET_SIZE: parseInt(getEnvVar('TOUCH_TARGET_SIZE', '48'), 10),
};

/**
 * Helper function to get environment variable with fallback
 * In browser context, variables should be injected at build time or via script tag
 * 
 * @param {string} key - Environment variable name
 * @param {string} defaultValue - Default value if not found
 * @returns {string} Environment variable value or default
 */
function getEnvVar(key, defaultValue) {
  // Try window.ENV object (injected via script tag)
  if (typeof window !== 'undefined' && window.ENV && window.ENV[key] !== undefined) {
    return window.ENV[key];
  }
  
  // CRA's webpack replaces the `process.env` expression with an inlined
  // object at build time, but the bare `process` identifier does not exist
  // in the browser — so it can't be referenced through a typeof guard
  // (that guard is always false in production builds and silently disables
  // every env-driven flag). Use try/catch instead.
  try {
    const env = process.env;
    if (env[key] !== undefined) {
      return env[key];
    }
    // Create React App only exposes REACT_APP_-prefixed variables
    if (env[`REACT_APP_${key}`] !== undefined) {
      return env[`REACT_APP_${key}`];
    }
  } catch (e) {
    // process is not defined in this environment — fall through to default
  }

  // Return default
  return defaultValue;
}

/**
 * Validation function to ensure required config is present
 * Call this on app initialization
 */
export function validateConfig() {
  const required = ['APP_NAME', 'APP_VERSION'];
  const missing = required.filter(key => !ENV_CONFIG[key]);
  
  if (missing.length > 0) {
    console.warn('Missing required environment variables:', missing);
  }
  
  // Log current environment in development
  if (ENV_CONFIG.IS_DEVELOPMENT) {
    console.log('🔧 Environment Configuration:', {
      environment: ENV_CONFIG.NODE_ENV,
      version: ENV_CONFIG.APP_VERSION,
      features: {
        offline: ENV_CONFIG.ENABLE_OFFLINE,
        analytics: ENV_CONFIG.ENABLE_ANALYTICS,
        pdfExport: ENV_CONFIG.ENABLE_PDF_EXPORT,
        bookmarks: ENV_CONFIG.ENABLE_BOOKMARKS,
        notes: ENV_CONFIG.ENABLE_NOTES,
      }
    });
  }
}

// Export individual configs for convenience
export const {
  APP_URL,
  APP_NAME,
  APP_VERSION,
  ENABLE_OFFLINE,
  ENABLE_ANALYTICS,
  ENABLE_PDF_EXPORT,
  ENABLE_BOOKMARKS,
  ENABLE_NOTES,
  IS_PRODUCTION,
  IS_DEVELOPMENT,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  MAX_SEARCH_RESULTS,
  SCROLL_THRESHOLD_BACK_TO_TOP,
  ANIMATION_DURATION_MS,
  TOUCH_TARGET_SIZE,
} = ENV_CONFIG;
